import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import moment from 'moment';
import { Button, Layout, Typography, message } from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import {
  BufferEventType,
  ComposerContainerMode,
  ComposerMode,
  EventComposer,
  EventComposerConfig,
  type EventComposerFormValues,
  EventDataResponse,
  EventDetailsContent,
  EventDetailsSuccessAction,
  EventDetailsSuccessActions,
  EventToBeCreated,
  type OnTrackComposerEvent,
  TimeFormatsConstants,
  type TimeFormats,
  useEventComposerConfig,
  useTrackComposerEvent,
} from '@robinpowered/dashboard-apps-patterns';
import {
  type EventParamMode,
  formatEventParam,
  parseEventParam,
  useGetUserSetting,
} from '@robinpowered/dashboard-apps-common';
import { getActiveAppNameForPathname } from '../../single-spa-config';
import { useTranslation } from 'react-i18next';
import { useApolloContext, useAuthContext, useAmplitude } from 'contexts';
import { useAdaptedUserAndOrg } from './useAdaptedUserAndOrg';
import { Sentry } from 'lib/sentry';
import { config } from 'config';

const { Sider } = Layout;

const SIDER_WIDTH = 380;

/**
 * Shell-level right-hand sidebar that hosts `EventDetails` or `EventComposer`
 * from `@robinpowered/dashboard-apps-patterns`. Visibility and contents are
 * derived from the canonical `event` query param (see
 * `common/common/src/utilities/eventParam.ts`).
 *
 * The Sider is a layout primitive that sits next to the MFE mount container;
 * opening/closing it never mutates the pathname, so the underlying MFE is
 * unaffected.
 */
export const EventOverlayHostLegacy = (): JSX.Element | null => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantId } = useApolloContext();
  const { currentUser, currentOrg } = useAuthContext();
  const { trackEvent } = useAmplitude();
  const [messageApi, contextHolder] = message.useMessage();

  const parsed = useMemo(
    () => parseEventParam(searchParams.get('event')),
    [searchParams]
  );
  const { mode, eventId } = parsed;
  const isOpen = mode !== null;

  // Stable, composer-shaped user/org — see useAdaptedUserAndOrg for the
  // identity-stability rationale.
  const { adaptedUser, adaptedOrg } = useAdaptedUserAndOrg(
    currentUser,
    currentOrg
  );

  const composerConfig = useEventComposerConfig({
    tenantId,
    currentUser: adaptedUser,
    currentOrg: adaptedOrg,
    sessionCookieName: config.sessionCookieName,
    urls: {
      featureFlagUrl: config.featureFlagUrl,
      integrationsServiceUrl: config.integrationsServiceUrl,
      apiUrl: config.apiUrl,
      baseApiUrl: config.baseApiUrl,
      dashboardUrl: config.dashboardUrl,
      imageServerUrl: config.imageServerUrl,
    },
  });

  /**
   * Mutate the `event` query param while preserving every other param and the
   * pathname. Pass `null` to remove the param entirely (close the sidebar).
   */
  const setEventParam = useCallback(
    (next: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (next === null) {
        params.delete('event');
      } else {
        params.set('event', next);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleClose = useCallback(() => setEventParam(null), [setEventParam]);

  const handleClickEdit = useCallback(
    (id: string) => {
      setEventParam(formatEventParam({ mode: 'edit', eventId: id }));
    },
    [setEventParam]
  );

  const handleSubmit = useCallback(
    (event: EventDataResponse) => {
      // After a successful create or edit, drop back to details mode on the
      // (possibly new) event id.
      const id = String(event.id);
      setEventParam(formatEventParam({ mode: 'details', eventId: id }));
    },
    [setEventParam]
  );

  const handleError = useCallback(
    (error: Error) => {
      Sentry.captureException(error);
      messageApi.error(error.message || 'An error occurred');
    },
    [messageApi]
  );

  const handleTrackBufferEvent = useCallback(
    (eventType: BufferEventType, metadata?: Record<string, unknown>) => {
      // Buffer event names mirror schedule's mapping (kept in sync manually
      // until we share an Amplitude event registry).
      const amplitudeEventName: Record<BufferEventType, string> = {
        addBuffer: 'added_buffer',
        removeBuffer: 'removed_buffer',
        createdBufferEvent: 'created_buffer_event',
        detectedBufferConflict: 'detected_buffer_conflict',
      };
      trackEvent(amplitudeEventName[eventType], metadata);
    },
    [trackEvent]
  );

  // Entry point = the MFE currently mounted under the overlay, resolved from
  // the single-spa registry (same approach as EventOverlayHost).
  const { pathname } = useLocation();
  const entryPoint = getActiveAppNameForPathname(pathname) ?? 'unknown';
  const handleTrackComposerEvent = useTrackComposerEvent({
    trackEvent,
    entryPoint,
  });

  const selectedTimezone = currentUser?.timeZone || moment.tz.guess();

  // Mirror the schedule app's locale logic: read the user's "24-hour-time"
  // setting and map to the corresponding TimeFormats enum value. Skips until
  // both userId and tenantId are present; until then we fall back to the
  // 12-hour default (matches the schedule LocalizationContext default).
  const { data: hourFormatData } = useGetUserSetting(
    currentUser?.id,
    tenantId,
    '24-hour-time'
  );
  const timeFormat: TimeFormats = useMemo(() => {
    const raw = hourFormatData?.getUserSetting?.value;
    if (!raw) return TimeFormatsConstants.TWELVE_HOUR;
    try {
      return JSON.parse(raw) === true
        ? TimeFormatsConstants.TWENTY_FOUR_HOUR
        : TimeFormatsConstants.TWELVE_HOUR;
    } catch {
      return TimeFormatsConstants.TWELVE_HOUR;
    }
  }, [hourFormatData]);

  // The "initial" event for CREATE mode — derived once from URL prefill query
  // params. Used as the immutable reference EventComposer's `initialEvent`
  // prop, which it reads to seed react-hook-form's defaults.
  const initialEvent = useMemo<EventToBeCreated | null>(() => {
    if (mode !== 'create') return null;
    return buildInitialCreateEvent(searchParams, selectedTimezone);
  }, [mode, searchParams, selectedTimezone]);

  // Controlled CREATE-mode event state. EventComposer treats its `event` prop
  // as the source of truth for the date — it runs a sync effect that snaps
  // the form's date back to `event.date` any time they diverge (see
  // common/patterns/.../CreateEventContext.tsx). If we keep passing the
  // stable `initialEvent` for `event`, every date pick gets reverted on the
  // next render. So we mirror the form values back into state via
  // `onEventChange`, and pass that state as `event`.
  const [createEvent, setCreateEvent] = useState<EventToBeCreated | null>(null);
  useEffect(() => {
    // (Re)seed the controlled state whenever the URL-derived `initialEvent`
    // changes — i.e. when we enter CREATE mode or when a prefill param
    // changes. Leaving CREATE mode resets to null so the next entry starts
    // fresh instead of inheriting the previous draft.
    setCreateEvent(initialEvent);
  }, [initialEvent]);

  const handleCreateEventChange = useCallback(
    (values: EventComposerFormValues) => {
      setCreateEvent((prev) => {
        if (!prev) return prev;
        // Only echo back the fields the composer actually syncs from the
        // parent — currently just `date`. Title/startTime/endTime/etc. are
        // owned by react-hook-form internally and don't have a sync-from-
        // parent effect, so we don't need to mirror them here. If patterns
        // adds new sync effects (e.g. for startTime), extend this.
        if (prev.date.isSame(values.date)) return prev;
        return { ...prev, date: values.date };
      });
    },
    []
  );

  // Stable noop ref for the edit-mode onEventChange handler.
  const noopRef = useRef(() => {});

  // Single always-mounted Sider that toggles its `collapsed` state based on
  // the `event` query param. This mirrors `NavSidebar`'s pattern so the Sider
  // is a true flex sibling of `NavSidebar` and `MainContainer` in `AppShell`'s
  // Layout — it pushes the main content rather than floating over it.
  return (
    <>
      {contextHolder}
      <RightSider
        collapsed={!isOpen}
        collapsedWidth={0}
        width={SIDER_WIDTH}
        trigger={null}
        reverseArrow
      >
        {isOpen
          ? renderBody({
              mode: mode as EventParamMode,
              eventId,
              composerConfig,
              initialEvent,
              createEvent,
              handleCreateEventChange,
              selectedTimezone,
              timeFormat,
              handleClose,
              handleClickEdit,
              handleSubmit,
              handleError,
              handleTrackBufferEvent,
              handleTrackComposerEvent,
              noop: noopRef.current,
            })
          : null}
      </RightSider>
    </>
  );
};

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

type RenderArgs = {
  mode: EventParamMode;
  eventId: string | null;
  composerConfig: EventComposerConfig | undefined;
  /** URL-derived event for CREATE mode — stable, used for `initialEvent`. */
  initialEvent: EventToBeCreated | null;
  /** Controlled event state for CREATE mode — updated via onEventChange. */
  createEvent: EventToBeCreated | null;
  handleCreateEventChange: (values: EventComposerFormValues) => void;
  selectedTimezone: string;
  timeFormat: (typeof TimeFormatsConstants)[keyof typeof TimeFormatsConstants];
  handleClose: () => void;
  handleClickEdit: (id: string) => void;
  handleSubmit: (event: EventDataResponse) => void;
  handleError: (error: Error) => void;
  handleTrackBufferEvent: (
    eventType: BufferEventType,
    metadata?: Record<string, unknown>
  ) => void;
  handleTrackComposerEvent: OnTrackComposerEvent;
  noop: () => void;
};

const renderBody = ({
  mode,
  eventId,
  composerConfig,
  initialEvent,
  createEvent,
  handleCreateEventChange,
  selectedTimezone,
  timeFormat,
  handleClose,
  handleClickEdit,
  handleSubmit,
  handleError,
  handleTrackBufferEvent,
  handleTrackComposerEvent,
  noop,
}: RenderArgs): JSX.Element | null => {
  if (!composerConfig) {
    // Auth / tenant / token still loading. The Sider is open per the URL but
    // the inner components can't render until config resolves; render null.
    return null;
  }

  if (mode === 'details') {
    if (!eventId) return null;
    return (
      <SiderPanelHeader onClose={handleClose}>
        <EventDetailsContent
          config={composerConfig}
          eventId={eventId}
          timezone={selectedTimezone}
          timeFormat={timeFormat}
          onClose={handleClose}
          onClickEdit={handleClickEdit}
          onDelete={noop}
          onError={handleError}
          onTrackComposerEvent={handleTrackComposerEvent}
          onSuccess={(action: EventDetailsSuccessAction) => {
            // For DELETE_EVENT we want to close the sidebar (the event is gone).
            if (action === EventDetailsSuccessActions.DELETE_EVENT) {
              handleClose();
            }
          }}
        />
      </SiderPanelHeader>
    );
  }

  if (mode === 'edit') {
    if (!eventId) return null;
    return (
      <EventComposer
        composerMode={ComposerMode.EDIT}
        containerMode={ComposerContainerMode.INLINE}
        config={composerConfig}
        event={{ id: eventId }}
        onClose={handleClose}
        onSubmit={handleSubmit}
        onError={(error) => handleError(error)}
        timeFormat={timeFormat}
        selectedTimezone={selectedTimezone}
        onTrackBufferEvent={handleTrackBufferEvent}
      />
    );
  }

  if (mode === 'create') {
    // `initialEvent` is the URL-derived starting point; `createEvent` is the
    // controlled state that follows user edits via `onEventChange`. Both must
    // be present — the controlled state is seeded from `initialEvent` in an
    // effect, so on the very first render after entering CREATE mode they
    // briefly differ and the composer can't render yet.
    if (!initialEvent || !createEvent) return null;
    return (
      <EventComposer
        composerMode={ComposerMode.CREATE}
        containerMode={ComposerContainerMode.INLINE}
        config={composerConfig}
        initialEvent={initialEvent}
        event={createEvent}
        onEventChange={handleCreateEventChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        onError={(error) => handleError(error)}
        timeFormat={timeFormat}
        selectedTimezone={selectedTimezone}
        onTrackBufferEvent={handleTrackBufferEvent}
      />
    );
  }

  return null;
};

const buildInitialCreateEvent = (
  searchParams: URLSearchParams,
  selectedTimezone: string
): EventToBeCreated => {
  const tz = searchParams.get('timezone') || selectedTimezone;
  const startedAtParam = searchParams.get('started_at');
  const endedAtParam = searchParams.get('ended_at');

  const startTime = startedAtParam
    ? moment.tz(startedAtParam, tz)
    : moment().tz(tz);
  const endTime = endedAtParam
    ? moment.tz(endedAtParam, tz)
    : startTime.clone().add(30, 'minutes');

  const isAllDayParam = searchParams.get('is_all_day');
  const isPrivateParam = searchParams.get('is_private');
  const inviteeCountParam = searchParams.get('invitee_count');

  return {
    date: startTime.clone(),
    startTime,
    endTime,
    title: searchParams.get('title') ?? undefined,
    isAllDayEvent: isAllDayParam === 'true' || isAllDayParam === '1',
    isPrivate: isPrivateParam === 'true' || isPrivateParam === '1',
    uid: searchParams.get('event_uid') ?? undefined,
    attendeeCount: inviteeCountParam ? Number(inviteeCountParam) : undefined,
    spaceId: searchParams.get('space_id') ?? undefined,
    levelId: searchParams.get('level_id')
      ? Number(searchParams.get('level_id'))
      : undefined,
  };
};

/**
 * Header row that the shell renders above an `EventDetailsContent` body, since
 * the patterns component no longer carries its own Drawer title bar in inline
 * mode. Title text comes from the patterns translation namespace
 * (`meetingRooms` / `eventDetails.title`) to match the Drawer variant.
 */
const SiderPanelHeader = ({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element => {
  const { t } = useTranslation('meetingRooms');
  return (
    <>
      <PanelHeader>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('eventDetails.title', 'Event details')}
        </Typography.Title>
        <Button
          type="text"
          shape="default"
          size="middle"
          icon={<FontAwesomeIcon icon={faXmark} />}
          onClick={onClose}
          aria-label="Close"
        />
      </PanelHeader>
      <PanelBody>{children}</PanelBody>
    </>
  );
};

const RightSider = styled(Sider)`
  background: var(--ant-color-bg-container, #ffffff);
  border-left: 1px solid var(--ant-color-border-secondary, #f0f0f0);
  height: 100%;
  overflow: hidden;
  /* Inner flex column so the panel header + body stack vertically. */
  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--ant-color-border-secondary, #f0f0f0);
  flex-shrink: 0;
`;

const PanelBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;
